import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/meta";
import { TripCard } from "@/components/site/TripCard";
import { Seo } from "@/components/site/Seo";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal } from "lucide-react";

const ALL = "all";

export default function Trips() {
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const [trips, setTrips] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [duration, setDuration] = useState(ALL);
  const [price, setPrice] = useState(ALL);
  const [month, setMonth] = useState(ALL);
  const [avail, setAvail] = useState(ALL);
  useEffect(() => { const p = new URLSearchParams(params); if (q) p.set("q", q); else p.delete("q"); setParams(p, { replace: true }); if (q.trim().length > 2) { const t = setTimeout(() => track("Search", { search_string: q.trim() }), 800); return () => clearTimeout(t); } }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const category = params.get("category") || ALL;
  const [destination, setDestination] = useState(ALL);
  const [sort, setSort] = useState("recommended");

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);
  useEffect(() => {
    setLoading(true);
    const query = category !== ALL ? `?category=${category}` : "";
    api.get(`/trips${query}`).then(({ data }) => setTrips(data)).finally(() => setLoading(false));
  }, [category]);

  const destinations = useMemo(() => [...new Set(trips.map((t) => t.destination))], [trips]);

  const months = useMemo(() => [...new Set(trips.flatMap((t) => (t.departures || []).map((d) => d.date.slice(0, 7))))].sort(), [trips]);
  const nights = (t) => { const m = /(\d+)\s*Night/i.exec(t.duration || ""); const d = /(\d+)\s*Day/i.exec(t.duration || ""); return m ? Number(m[1]) : d ? Math.max(0, Number(d[1]) - 1) : 0; };
  const filtered = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = (t) => [t.name, t.destination, t.category, t.duration, t.overview, t.special_tag, t.festive_tag, t.creator_name, t.creator_handle, ...(t.highlights || []), ...(t.pickup_points || []), ...((t.itinerary || []).map((i) => i.title))].filter(Boolean).join(" ").toLowerCase();
    const creatorOnly = params.get("creator") === "1";
    let list = trips.filter((t) => {
      if (creatorOnly && !t.creator) return false;
      const h = hay(t);
      if (terms.length && !terms.every((w) => h.includes(w))) return false;
      if (destination !== ALL && t.destination !== destination) return false;
      const n = nights(t);
      if (duration === "1-2" && !(n >= 1 && n <= 2)) return false;
      if (duration === "3-4" && !(n >= 3 && n <= 4)) return false;
      if (duration === "5-7" && !(n >= 5 && n <= 7)) return false;
      if (duration === "8+" && !(n >= 8)) return false;
      const p = t.starting_price || 0;
      if (price === "u5" && p > 5000) return false;
      if (price === "5-10" && (p <= 5000 || p > 10000)) return false;
      if (price === "10-20" && (p <= 10000 || p > 20000)) return false;
      if (price === "20+" && p <= 20000) return false;
      if (month !== ALL && !(t.departures || []).some((d) => d.date.startsWith(month))) return false;
      if (avail === "open" && !t.bookable) return false;
      if (avail === "few" && !(t.bookable && t.min_seats_left != null && t.min_seats_left <= 5)) return false;
      return true;
    });
    if (sort === "price-low") list = [...list].sort((a, b) => a.starting_price - b.starting_price);
    if (sort === "price-high") list = [...list].sort((a, b) => b.starting_price - a.starting_price);
    return list;
  }, [trips, q, destination, sort, duration, price, month, avail, params]);

  const setCategory = (c) => {
    const p = new URLSearchParams(params);
    if (c === ALL) p.delete("category"); else p.set("category", c);
    setParams(p);
  };

  return (
    <div className="pt-16">
      <Seo title={settings.trips_seo_title || "All Trips, Treks & Yatras"} description={settings.trips_seo_description || "Browse all upcoming curated trips, Himalayan treks and dham yatras with dates and prices. Book with a small advance on WhatsApp."} />
      <div className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="uppercase tracking-[0.25em] text-gold text-sm">Explore</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold mt-2">All Trips</h1>
          <p className="text-secondary-foreground/70 mt-3 max-w-xl">Browse curated treks, trips and dham yatras. Filter to find the journey that fits you.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          <button onClick={() => setCategory(ALL)} data-testid="filter-cat-all"
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${category === ALL ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>All</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.slug)} data-testid={`filter-cat-${c.slug}`}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${category === c.slug ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{c.name}</button>
          ))}
        </div>

        {/* Filters row */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9 rounded-full h-11 text-base" placeholder="Search trips, destinations or experiences…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="trips-search" />
          </div>
          <Select value={destination} onValueChange={setDestination}>
            <SelectTrigger className="sm:w-48 rounded-full" data-testid="filter-destination"><SelectValue placeholder="Destination" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Destinations</SelectItem>
              {destinations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-48 rounded-full" data-testid="filter-sort"><SlidersHorizontal className="h-4 w-4 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recommended">Recommended</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="trip-filters">
          <Select value={duration} onValueChange={setDuration}><SelectTrigger className="w-auto min-w-[130px] rounded-full h-9 text-sm" data-testid="filter-duration"><SelectValue placeholder="Duration" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Any duration</SelectItem><SelectItem value="1-2">1–2 nights</SelectItem><SelectItem value="3-4">3–4 nights</SelectItem><SelectItem value="5-7">5–7 nights</SelectItem><SelectItem value="8+">8+ nights</SelectItem></SelectContent></Select>
          <Select value={price} onValueChange={setPrice}><SelectTrigger className="w-auto min-w-[130px] rounded-full h-9 text-sm" data-testid="filter-price"><SelectValue placeholder="Price" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Any price</SelectItem><SelectItem value="u5">Under ₹5,000</SelectItem><SelectItem value="5-10">₹5,000 – ₹10,000</SelectItem><SelectItem value="10-20">₹10,000 – ₹20,000</SelectItem><SelectItem value="20+">Above ₹20,000</SelectItem></SelectContent></Select>
          <Select value={month} onValueChange={setMonth}><SelectTrigger className="w-auto min-w-[140px] rounded-full h-9 text-sm" data-testid="filter-month"><SelectValue placeholder="Departure" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Any departure</SelectItem>{months.map((m) => <SelectItem key={m} value={m}>{new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" })}</SelectItem>)}</SelectContent></Select>
          <Select value={avail} onValueChange={setAvail}><SelectTrigger className="w-auto min-w-[130px] rounded-full h-9 text-sm" data-testid="filter-availability"><SelectValue placeholder="Availability" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Any availability</SelectItem><SelectItem value="open">Seats available</SelectItem><SelectItem value="few">Filling fast (≤5 left)</SelectItem></SelectContent></Select>
          {(q || destination !== ALL || duration !== ALL || price !== ALL || month !== ALL || avail !== ALL) && <Button variant="ghost" size="sm" className="rounded-full h-9 shrink-0" onClick={() => { setQ(""); setDestination(ALL); setDuration(ALL); setPrice(ALL); setMonth(ALL); setAvail(ALL); }} data-testid="filters-clear">Clear all</Button>}
        </div>

        <p className="mt-5 text-sm text-muted-foreground" data-testid="trips-count">{filtered.length} trip(s) found{q ? ` for “${q}”` : ""}</p>

        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 pb-20">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-96 rounded-2xl bg-muted animate-pulse" />)
          ) : filtered.length === 0 ? (
            <div className="col-span-full text-center py-20 text-muted-foreground" data-testid="trips-empty">
              <p className="font-heading text-3xl text-foreground">No trips found.</p>
              <p className="mt-1">Try searching another destination.</p>
              <Button className="mt-5 rounded-full" onClick={() => { setQ(""); setDestination(ALL); setCategory(ALL); setDuration(ALL); setPrice(ALL); setMonth(ALL); setAvail(ALL); }} data-testid="view-all-trips">View All Trips</Button>
            </div>
          ) : (
            filtered.map((t, i) => <TripCard key={t.id} trip={t} index={i} />)
          )}
        </div>
      </div>
    </div>
  );
}
